/**
 * @fileoverview Célula Editável da DRE
 * @module components/dre/DRECell
 * 
 * Célula individual da tabela DRE com edição inline
 */

import React, { useState, useEffect } from 'react';
import {
  TableCell,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { useDREStore } from '../../stores/dreStore';
import { formatCurrency, formatPercent } from '../../lib/dreCalculations';

interface DRECellProps {
  company: 'setima' | '555';
  year: number;
  field: string;
  value: number;
  editable: boolean;
  format: 'currency' | 'percent';
  negative?: boolean;
  calculated?: boolean;
  highlight?: boolean;
}

export function DRECell({
  company,
  year,
  field,
  value,
  editable,
  format,
  negative = false,
  calculated = false,
  highlight = false,
}: DRECellProps) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value.toString());
  const { updateField } = useDREStore();

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(localValue) || 0;
    if (numValue !== value) {
      updateField(company, year, field, numValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(value.toString());
      setIsEditing(false);
    }
  };

  const displayValue = format === 'currency' 
    ? formatCurrency(value) 
    : formatPercent(value);

  const isNegative = value < 0;
  const isWarning = field.includes('margem') && value < 0;

  const cellColor = isNegative || negative
    ? 'error.main'
    : isWarning
    ? 'warning.main'
    : calculated
    ? 'text.primary'
    : 'text.secondary';

  return (
    <TableCell
      align="right"
      sx={{
        bgcolor: highlight 
          ? (theme.palette.mode === 'dark' 
              ? 'rgba(129, 213, 204, 0.15)' 
              : 'rgba(129, 213, 204, 0.1)')
          : calculated 
          ? (theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.03)'
              : 'rgba(0, 0, 0, 0.02)')
          : 'transparent',
        cursor: editable && !calculated ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: editable && !calculated 
            ? (theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)')
            : undefined,
        },
      }}
      onClick={() => editable && !calculated && setIsEditing(true)}
    >
      {editable && !calculated && isEditing ? (
        <TextField
          type="number"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          size="small"
          fullWidth
          inputProps={{
            'aria-label': `${field} para ${company} ano ${year}`,
            'aria-describedby': `dre-cell-${company}-${year}-${field}`,
          }}
          sx={{
            '& input': {
              textAlign: 'right',
              fontFamily: 'monospace',
            },
          }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: cellColor,
            fontWeight: calculated ? 'bold' : 'normal',
            fontFamily: 'monospace',
          }}
        >
          {negative && value > 0 ? `(${displayValue})` : displayValue}
        </Typography>
      )}
    </TableCell>
  );
}
